import type { HttpResponse } from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { ContactResponse, Contact } from '@hunting-nature/shared-core/interfaces/contact-response.interface';
import { EMPTY, expand, map, reduce, type Observable } from 'rxjs';

import { AppConfigLoader } from '../../../config/app-config.loader';

import type {
  CameraPermission,
  CreateContactPayload,
  CreateContactPayloadRequest,
  ShareCameraPayload,
  ShareCameraPayloadRequest,
  UpdateCameraPermissionPayloadRequest,
  UpdateUsernamePayload,
} from './contacts.rest.types';

@Injectable({
  providedIn: 'root',
})
/**
 * HTTP adapter for the contacts backend. This file only knows about REST
 * payloads, headers, and response shaping so higher-level flows stay testable.
 */
export class ContactsRestService {
  // services
  private readonly http = inject(HttpClient);
  private readonly appConfigLoader = inject(AppConfigLoader);
  private readonly contactsPageLimit = 20;

  public addContact$(payload: CreateContactPayload): Observable<Contact> {
    const url = `${this.appConfigLoader.activeConfig().backend.apiUrl}/contacts`;
    const body: CreateContactPayloadRequest = {
      ...payload,
      sharedCameras: payload.sharedCameras.map((camera: CreateContactPayload['sharedCameras'][number]) => ({
        ...camera,
        permission: this.toLowerPermission(camera.permission),
      })),
    };

    return this.http.post<Contact>(url, body);
  }

  public shareCameraWithContact$(contactId: string, payload: ShareCameraPayload): Observable<Contact> {
    const url = `${this.appConfigLoader.activeConfig().backend.apiUrl}/contacts/${contactId}/cameras`;
    const body: ShareCameraPayloadRequest = {
      ...payload,
      permission: this.toLowerPermission(payload.permission),
    };

    return this.http.post<Contact>(url, body);
  }

  public updateUsername$(contactId: string, username: string): Observable<Contact> {
    const url = `${this.appConfigLoader.activeConfig().backend.apiUrl}/contacts/${contactId}`;
    const body: UpdateUsernamePayload = { alias: username };

    return this.http.patch<Contact>(url, body);
  }

  public removeCameraFromContact$(contactId: string, cameraId: string): Observable<void> {
    const url = `${this.appConfigLoader.activeConfig().backend.apiUrl}/contacts/${contactId}/cameras/${cameraId}`;
    return this.http.delete<void>(url);
  }

  public updateCameraContactPermission$(contactId: string, cameraId: string, permission: CameraPermission): Observable<Contact> {
    const url = `${this.appConfigLoader.activeConfig().backend.apiUrl}/contacts/${contactId}/cameras/${cameraId}`;
    const body: UpdateCameraPermissionPayloadRequest = {
      permission: this.toLowerPermission(permission),
    };
    return this.http.patch<Contact>(url, body);
  }

  public resendInvitation$(contactId: string): Observable<void> {
    const url = `${this.appConfigLoader.activeConfig().backend.apiUrl}/contacts/${contactId}/invitation/resend`;

    return this.http.post<void>(url, undefined);
  }

  public fetchContacts$(limit: number = 10, offset: number = 0): Observable<ContactResponse> {
    const params = { limit, offset };
    const url = `${this.appConfigLoader.activeConfig().backend.apiUrl}/contacts`;

    return this.http.get<Array<Contact>>(url, { params, observe: 'response' }).pipe(
      map((response: HttpResponse<Array<Contact>>) => {
        const contacts = response.body ?? [];
        const totalCount = Number.parseInt(response.headers.get('X-Total-Count') ?? '0', 10);

        return {
          contacts,
          totalCount,
        } as ContactResponse;
      })
    );
  }

  public fetchAllContacts$(): Observable<Array<Contact>> {
    return this.fetchContacts$(this.contactsPageLimit, 0).pipe(
      expand((response, index) => {
        const nextOffset = (index + 1) * this.contactsPageLimit;
        return nextOffset < response.totalCount ? this.fetchContacts$(this.contactsPageLimit, nextOffset) : EMPTY;
      }),
      map(response => response.contacts),
      reduce((all, page) => all.concat(page), [] as Array<Contact>)
    );
  }

  public deleteContact$(contactId: string): Observable<void> {
    const url = `${this.appConfigLoader.activeConfig().backend.apiUrl}/contacts/${contactId}`;

    return this.http.delete<void>(url);
  }

  private toLowerPermission(permission: CameraPermission) {
    return permission === 'VIEWER' ? 'viewer' : 'operator';
  }
}
